import type { TFunction } from 'i18next';
import {
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  Clock,
  Globe2,
  ListTree,
  Loader2,
  Wrench,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { getToolDisplayName } from '@/modules/chat/application/chat-tool-registry';
import {
  buildProcessStepTree,
  getActiveApprovalCall,
  getVisibleProcessStepTree,
  getWorkLogDurationFromRange,
  getWorkLogDurationRange,
  getWorkLogProcessSteps,
  nodeContainsActiveApproval,
  normalizeToolProvider,
} from '@/modules/chat/application/chat-work-log';
import type {
  ChatMessageWithProcessSteps,
  ChatProcessStep,
  ChatToolCall,
  ProcessStepNode,
} from '@/modules/chat/application/chat-work-log';
import {
  ChatProcessStepStatus,
  ChatProcessStepType,
  ChatToolProvider,
} from '@/modules/common/types';

import { ToolCallWorkLogDetails } from './ToolCallDetails';

const PROCESS_STEP_SUMMARY_KEYS = new Map([
  ['Tools used by the assistant.', 'modules.mavue2.chat.workLog.providerSummary'],
  [
    'Approved. Waiting for the remaining approvals before continuing.',
    'modules.mavue2.chat.workLog.summaries.approvedWaitingRemaining',
  ],
  [
    'Approved. Waiting for tool execution to start.',
    'modules.mavue2.chat.workLog.summaries.approvedWaitingExecution',
  ],
  [
    'Waiting for your approval before continuing.',
    'modules.mavue2.chat.workLog.summaries.waitingApproval',
  ],
  [
    'Waiting for the remaining approvals before continuing.',
    'modules.mavue2.chat.workLog.summaries.waitingRemainingApprovals',
  ],
  [
    'Approval responses recorded. Waiting for the assistant to continue.',
    'modules.mavue2.chat.workLog.summaries.approvalResponsesRecorded',
  ],
]);

const PROCESS_STEP_STATUS_VIEW: Record<ChatProcessStepStatus, { labelKey: string; tone: string }> =
  {
    [ChatProcessStepStatus.RUNNING]: {
      labelKey: 'modules.mavue2.chat.workLog.statuses.running',
      tone: 'text-blue-700',
    },
    [ChatProcessStepStatus.AWAITING_APPROVAL]: {
      labelKey: 'modules.mavue2.chat.workLog.statuses.approvalRequired',
      tone: 'text-amber-700',
    },
    [ChatProcessStepStatus.COMPLETED]: {
      labelKey: 'modules.mavue2.chat.workLog.statuses.completed',
      tone: 'text-green-700',
    },
    [ChatProcessStepStatus.ERROR]: {
      labelKey: 'modules.mavue2.chat.workLog.statuses.error',
      tone: 'text-red-700',
    },
    [ChatProcessStepStatus.DENIED]: {
      labelKey: 'modules.mavue2.chat.workLog.statuses.denied',
      tone: 'text-red-700',
    },
    [ChatProcessStepStatus.PENDING]: {
      labelKey: 'modules.mavue2.chat.workLog.statuses.pending',
      tone: 'text-muted-foreground',
    },
  };

const PROVIDER_LABEL_KEY: Record<ChatToolProvider, string> = {
  [ChatToolProvider.MAVUE]: 'modules.mavue2.chat.workLog.providers.mavue',
  [ChatToolProvider.WEB_SEARCH]: 'modules.mavue2.chat.workLog.providers.webSearch',
};

function getProcessStepStatus(step: ChatProcessStep, t: TFunction) {
  const view = PROCESS_STEP_STATUS_VIEW[step.status];

  return { label: t(view.labelKey), tone: view.tone };
}

function getToolProviderWorkLogLabel(step: ChatProcessStep, t: TFunction) {
  const provider = normalizeToolProvider(step.provider, step.title);

  return provider ? t(PROVIDER_LABEL_KEY[provider]) : step.title;
}

function getProcessStepTitle({
  step,
  linkedToolCall,
  t,
}: {
  step: ChatProcessStep;
  linkedToolCall: ChatToolCall | null;
  t: TFunction;
}) {
  if (step.type === ChatProcessStepType.TOOL_PROVIDER) {
    return getToolProviderWorkLogLabel(step, t);
  }

  if (linkedToolCall) {
    return getToolDisplayName(linkedToolCall, t);
  }

  return step.title;
}

function getProcessStepSummary({
  step,
  linkedToolCall,
  t,
}: {
  step: ChatProcessStep;
  linkedToolCall: ChatToolCall | null;
  t: TFunction;
}) {
  if (!step.summary) {
    return null;
  }

  const summaryKey = PROCESS_STEP_SUMMARY_KEYS.get(step.summary);

  if (summaryKey) {
    return t(summaryKey);
  }

  const calledToolMatch = step.summary.match(/^Called (.+)\.$/);

  if (calledToolMatch) {
    return t('modules.mavue2.chat.workLog.calledTool', {
      tool: linkedToolCall ? getToolDisplayName(linkedToolCall, t) : calledToolMatch[1],
    });
  }

  return step.summary;
}

function getProcessStepIcon(step: ChatProcessStep) {
  if (step.status === ChatProcessStepStatus.RUNNING) {
    return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  }

  if (step.status === ChatProcessStepStatus.ERROR || step.status === ChatProcessStepStatus.DENIED) {
    return <CircleAlert className="h-4 w-4 text-red-600" />;
  }

  if (step.status === ChatProcessStepStatus.COMPLETED) {
    return <CircleCheck className="h-4 w-4 text-green-600" />;
  }

  if (normalizeToolProvider(step.provider, step.title) === ChatToolProvider.WEB_SEARCH) {
    return <Globe2 className="h-4 w-4 text-muted-foreground" />;
  }

  if (step.type === ChatProcessStepType.TOOL_PROVIDER) {
    return <ListTree className="h-4 w-4 text-muted-foreground" />;
  }

  if (step.type === ChatProcessStepType.TOOL_CALL) {
    return <Wrench className="h-4 w-4 text-muted-foreground" />;
  }

  return <Clock className="h-4 w-4 text-muted-foreground" />;
}

function ProcessStepRow({
  step,
  toolCallsById,
  activeApprovalCallId,
  disabled,
  onApproval,
  t,
  depth = 0,
}: {
  step: ProcessStepNode;
  toolCallsById: Map<string, ChatToolCall>;
  activeApprovalCallId: string | null;
  disabled: boolean;
  onApproval: (call: ChatToolCall, approved: boolean) => void;
  t: TFunction;
  depth?: number;
}) {
  const linkedToolCall = step.chatToolCallId
    ? (toolCallsById.get(step.chatToolCallId) ?? null)
    : null;
  const isActiveApproval = !!linkedToolCall && linkedToolCall.id === activeApprovalCallId;
  const hasChildren = step.children.length > 0;
  const hasActiveChild = step.children.some(
    (child) =>
      child.status === ChatProcessStepStatus.RUNNING ||
      nodeContainsActiveApproval(child, activeApprovalCallId)
  );
  const shouldAutoOpen =
    isActiveApproval || (step.type === ChatProcessStepType.TOOL_PROVIDER && hasActiveChild);
  const [open, setOpen] = useState(
    isActiveApproval ||
      step.type === ChatProcessStepType.TOOL_PROVIDER ||
      (!linkedToolCall && step.status === ChatProcessStepStatus.RUNNING)
  );
  const wasActiveApprovalRef = useRef(isActiveApproval);
  const status = getProcessStepStatus(step, t);
  const title = getProcessStepTitle({ step, linkedToolCall, t });
  const summary = getProcessStepSummary({ step, linkedToolCall, t });

  useEffect(() => {
    if (shouldAutoOpen) {
      setOpen(true);
    }
  }, [shouldAutoOpen]);

  useEffect(() => {
    if (wasActiveApprovalRef.current && !isActiveApproval) {
      setOpen(false);
    }

    wasActiveApprovalRef.current = isActiveApproval;
  }, [isActiveApproval]);

  const content = (
    <div className="flex min-w-0 flex-1 items-start gap-2">
      <div className="mt-0.5 shrink-0">{getProcessStepIcon(step)}</div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="break-words text-sm font-medium text-foreground">{title}</span>
          <span className={`text-xs ${status.tone}`}>{status.label}</span>
        </div>
        {summary && <div className="mt-0.5 text-xs leading-5 text-muted-foreground">{summary}</div>}
      </div>
    </div>
  );

  return (
    <div className={depth > 0 ? 'ml-5 border-l border-border/70 pl-3' : undefined}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className="rounded-md px-1 py-1.5">
          {hasChildren || linkedToolCall ? (
            <CollapsibleTrigger asChild>
              <button type="button" className="flex w-full items-start gap-1 text-left">
                {open ? (
                  <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                {content}
              </button>
            </CollapsibleTrigger>
          ) : (
            <div className="ml-5">{content}</div>
          )}

          <CollapsibleContent>
            {linkedToolCall && (
              <ToolCallWorkLogDetails
                call={linkedToolCall}
                disabled={disabled}
                onApproval={onApproval}
                isActiveApproval={isActiveApproval}
                t={t}
              />
            )}

            {hasChildren && (
              <div className="mt-1 space-y-1">
                {step.children.map((child) => (
                  <ProcessStepRow
                    key={child.id}
                    step={child}
                    toolCallsById={toolCallsById}
                    activeApprovalCallId={activeApprovalCallId}
                    disabled={disabled}
                    onApproval={onApproval}
                    t={t}
                    depth={depth + 1}
                  />
                ))}
              </div>
            )}
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
}

function WorkLogDurationLabel({
  startedAt,
  endedAt,
  isLive,
  labelKey,
  t,
}: {
  startedAt: number;
  endedAt: number | null;
  isLive: boolean;
  labelKey: string;
  t: TFunction;
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isLive) {
      return;
    }

    setNow(Date.now());
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isLive, startedAt]);

  const duration = getWorkLogDurationFromRange({ startedAt, endedAt, now });

  return <span>{t(labelKey, { duration })}</span>;
}

export function AssistantWorkLog({
  message,
  open,
  ticking,
  disabled,
  onApproval,
  t,
}: {
  message: ChatMessageWithProcessSteps;
  open: boolean;
  ticking: boolean;
  disabled: boolean;
  onApproval: (call: ChatToolCall, approved: boolean) => void;
  t: TFunction;
}) {
  const toolCalls = message.toolCalls ?? [];
  const processSteps = getWorkLogProcessSteps({ message, toolCalls });
  const activeApprovalCall = getActiveApprovalCall({ toolCalls, processSteps });
  const [isOpen, setIsOpen] = useState(open);
  const previousMessageIdRef = useRef(message.id);
  const previousOpenRef = useRef(open);
  const toolCallsById = new Map(toolCalls.map((call) => [call.id, call]));
  const tree = getVisibleProcessStepTree({
    nodes: buildProcessStepTree(processSteps),
    toolCallsById,
    activeApprovalCallId: activeApprovalCall?.id ?? null,
  });
  const durationRange = getWorkLogDurationRange({
    message,
    processSteps,
    isLive: ticking,
  });
  const headerLabelKey = ticking
    ? 'modules.mavue2.chat.workLog.workingFor'
    : 'modules.mavue2.chat.workLog.workedFor';

  useEffect(() => {
    if (previousMessageIdRef.current !== message.id) {
      previousMessageIdRef.current = message.id;
      previousOpenRef.current = open;
      setIsOpen(open);
      return;
    }

    if (open) {
      setIsOpen(true);
    } else if (previousOpenRef.current) {
      setIsOpen(false);
    }

    previousOpenRef.current = open;
  }, [message.id, open]);

  if (processSteps.length === 0) {
    return null;
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (open && !nextOpen) {
      return;
    }

    setIsOpen(nextOpen);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={handleOpenChange} className="mb-4">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2 text-left text-sm text-muted-foreground"
        >
          <WorkLogDurationLabel
            startedAt={durationRange.startedAt}
            endedAt={durationRange.endedAt}
            isLive={ticking}
            labelKey={headerLabelKey}
            t={t}
          />
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
      </CollapsibleTrigger>
      <div className="mt-3 border-t border-border/70" />

      <CollapsibleContent>
        <div className="mt-3 space-y-1">
          {tree.map((step) => (
            <ProcessStepRow
              key={step.id}
              step={step}
              toolCallsById={toolCallsById}
              activeApprovalCallId={activeApprovalCall?.id ?? null}
              disabled={disabled}
              onApproval={onApproval}
              t={t}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
